import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

// Stage definitions for the 3-step progress bar shown during chat round-trips.
//   id      Stable key for React.
//   label   Human-readable banner text.
//   from    Progress floor (0..100) when this stage activates.
//   to      Progress ceiling — animated linearly until the next stage takes over.
//   ms      Time budget to reach `to` before advancing (ignored for the final
//           stage which "pins" at `from` until the parent unmounts the overlay).
const STAGES = [
  { id: "analyze",  label: "Analyzing Request",   from: 0,  to: 30, ms: 3000 },
  { id: "drafting", label: "Drafting Layout",     from: 30, to: 70, ms: 5000 },
  { id: "render",   label: "Finalizing 3D Model", from: 70, to: 90, ms: 4000 },
];

/**
 * Client-side animated progress bar shown while a chat round-trip is in
 * flight. Auto-advances through 3 stages on a fixed timer (Gemini doesn't
 * expose true streaming for our blueprint use case — the perceived
 * progress is what matters here). When the parent flips `loading` from
 * true → false, the bar snaps to 100% and the overlay fades out.
 *
 * Mounted by Editor. Sits ABOVE the ThreeViewport but does NOT block the
 * 3D canvas from rebuilding underneath, because pointer-events:none is
 * applied to the wrapper after the fade-out begins.
 */
export default function LoadingOverlay({ loading, continuousLabel = null }) {
  const [pct, setPct] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);
  const [done, setDone] = useState(false);
  const startRef = useRef(0);
  const rafRef = useRef(null);
  const stageStartRef = useRef(0);

  // Drive progress via rAF — smooth, frame-locked, no layout thrash.
  useEffect(() => {
    if (!loading) {
      // Snap to 100% on completion, then fade out the overlay after a beat.
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setPct(100);
      setDone(true);
      const t = setTimeout(() => {
        setDone(false);
        setPct(0);
        setStageIdx(0);
      }, 700);
      return () => clearTimeout(t);
    }

    setDone(false);
    setStageIdx(0);
    setPct(0);
    startRef.current = performance.now();
    stageStartRef.current = startRef.current;

    const tick = () => {
      const now = performance.now();
      // Resolve the active stage by elapsed time.
      const elapsed = now - startRef.current;
      let acc = 0;
      let active = STAGES.length - 1;
      for (let i = 0; i < STAGES.length; i++) {
        acc += STAGES[i].ms;
        if (elapsed < acc) { active = i; break; }
      }
      // Compute the within-stage progress and map to the global pct range.
      const stage = STAGES[active];
      const stageStart = STAGES.slice(0, active).reduce((s, x) => s + x.ms, 0);
      const stageElapsed = elapsed - stageStart;
      const localT = Math.max(0, Math.min(1, stageElapsed / stage.ms));
      // Pin at the final stage's `from` instead of progressing all the way.
      const isFinal = active === STAGES.length - 1;
      const target = isFinal
        ? stage.from + (stage.to - stage.from) * Math.min(localT, 0.9)
        : stage.from + (stage.to - stage.from) * localT;
      setPct(target);
      setStageIdx(active);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [loading]);

  if (!loading && !done) return null;
  const activeStage = STAGES[Math.min(stageIdx, STAGES.length - 1)];
  const fading = !loading && done;

  return (
    <div
      data-testid="loading-overlay-backdrop"
      className={
        "absolute inset-0 z-30 grid place-items-center pointer-events-none " +
        "bg-zinc-950/55 backdrop-blur-[2px] transition-opacity duration-500 " +
        (fading ? "opacity-0" : "opacity-100")
      }
    >
      <div
        data-testid="loading-overlay"
        className="w-[min(440px,calc(100%-2rem))] border border-brass-500/50 bg-zinc-950/95 shadow-[0_24px_72px_rgba(0,0,0,0.75)]"
        role="status"
        aria-live="polite"
      >
        {/* Brand mark + headline */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-3">
          <div className="relative w-9 h-9 grid place-items-center border border-brass-500/60 bg-brass-950/30">
            <Loader2 className="w-4 h-4 animate-spin text-brass-400" />
            <span className="absolute inset-0 border border-brass-400/30 animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-brass-500/80">
              // Blueprint AI
            </div>
            <div
              data-testid="loading-overlay-stage"
              className="font-display text-base text-zinc-50 truncate"
            >
              {continuousLabel
                ? `Building ${continuousLabel}`
                : activeStage.label}
            </div>
          </div>
          <div
            data-testid="loading-overlay-percent"
            className="font-mono text-sm text-brass-300 tabular-nums"
          >
            {Math.round(pct)}%
          </div>
        </div>
        <div className="h-1.5 mx-5 mb-4 bg-zinc-900 relative overflow-hidden">
          <div
            data-testid="loading-overlay-fill"
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-brass-500 via-brass-400 to-orange-400 transition-[width] duration-200"
            style={{ width: `${pct}%` }}
          />
          {/* Subtle moving gloss highlight while in flight */}
          {!fading && (
            <div
              className="absolute inset-y-0 w-12 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_1.6s_linear_infinite]"
              style={{ left: `calc(${pct}% - 3rem)` }}
            />
          )}
        </div>
        <div className="flex items-center justify-between px-5 pb-4 gap-3">
          {STAGES.map((s, i) => (
            <div
              key={s.id}
              data-testid={`loading-stage-pip-${s.id}`}
              className={
                "flex-1 font-mono text-[9px] uppercase tracking-widest leading-snug " +
                (i < stageIdx
                  ? "text-brass-500"
                  : i === stageIdx
                  ? "text-orange-300"
                  : "text-zinc-600")
              }
            >
              <div>
                {i < stageIdx ? "✓ " : i === stageIdx ? "▸ " : "○ "}
                {s.label.split(" ")[0]}
              </div>
              <div className="text-[8px] tracking-normal text-zinc-600 normal-case">
                {s.label.split(" ").slice(1).join(" ")}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
